<script setup lang="ts">
import { NuxtLink } from "#components";
import routerOptions from "~/app/router.options";
import getUrlPrefix from "~/components/cms/helpers/getUrlPrefix";

interface SeoUrl {
  isCanonical: boolean;
  seoPathInfo: string;
}

interface ButtonConfig {
  url: string | null;
  type: "internal" | "external" | null;
  title: string;
  entity: string | null;
  newTab: boolean;
  entity_id: string | null;
  link?: {
    seoUrls?: SeoUrl[];
  };
}

const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const route = useRoute();

const { languageIdChain } = useSessionContext();

const props = defineProps<{
  button: ButtonConfig;
  class: string;
  isInnerHtml?: Boolean;
  activeClass?: string;
}>();

const buttonConfig = props.button;
const btnClass = props.class;
const isInnerHtml = props.isInnerHtml;
const activeClass = props.activeClass;

const getButtonUrl = (config: ButtonConfig) => {
  if (config.type === "external" && config.url) {
    return config.url;
  }

  if (config.link?.seoUrls?.length) {
    const canonicalUrl = config.link.seoUrls.find(
      (url) =>
        url.isCanonical === true && url.languageId === languageIdChain.value
    );
    if (canonicalUrl) {
      return formatLink("/" + canonicalUrl.seoPathInfo);
    }
  }

  if (config.url) {
    return config.url;
  }

  return "#";
};

const buttonUrl = computed(() => getButtonUrl(buttonConfig));

const isActiveLink = computed(() => buttonUrl.value === route.path);

const buttonClasses = computed(() => ({
  [btnClass]: true,
  [activeClass || ""]: isActiveLink.value,
}));
</script>

<template>
  <NuxtLink
    v-if="button?.title"
    :to="buttonUrl"
    :external="buttonConfig.type === 'external' ? true : false"
    :title="buttonConfig.title"
    :target="buttonConfig.newTab ? '_blank' : '_self'"
    :class="buttonClasses"
  >
    {{ button.title }}
    <template v-if="isInnerHtml">
      <span class="ml-3"><i class="fa-solid fa-angle-right"></i></span>
    </template>
  </NuxtLink>
</template>
